import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {ColumnDef} from "@tanstack/react-table";
import {DataTable} from "@/lib/components/DataTable";
import {QuotesByMonthDTO} from "@/api/data-contracts";
import {ReportI18N} from "@/translations";

interface QuotesByMonthReportProps {
    data: QuotesByMonthDTO[];
}

interface QuotesByMonthRow extends QuotesByMonthDTO {
    id: number;
}

/**
 * Report table showing quote counts per calendar month.
 */
const QuotesByMonthReport: React.FC<QuotesByMonthReportProps> = ({ data }) => {
    const { t } = useTranslation();

    const rows = useMemo<QuotesByMonthRow[]>(
        () => data.map((item, index) => ({ ...item, id: index })),
        [data]
    );

    const columns = useMemo<ColumnDef<QuotesByMonthRow>[]>(
        () => [
            {
                header: t(ReportI18N.page.column.year),
                accessorFn: (row) => row.year ?? "",
                id: "year"
            },
            {
                header: t(ReportI18N.page.column.month),
                accessorFn: (row) => row.month ?? "",
                id: "month"
            },
            {
                header: t(ReportI18N.page.column.count),
                accessorFn: (row) => row.quoteCount ?? 0,
                id: "quoteCount"
            }
        ],
        [t]
    );

    return <DataTable columns={columns} data={rows} noResultsValue="" striped maxHeight={940} />;
};

export default QuotesByMonthReport;
